import {
  AssistantType,
  FunctionInterface,
  FunctionParameterInterface,
  Namespace,
  NamespaceInterface,
  VendorModel,
} from '@vendasta/ai-assistants';

import {
  ASSISTANT_ID_CHAT_RECEPTIONIST,
  ASSISTANT_ID_CONTENT_CREATOR,
  ASSISTANT_ID_SALES_COACH,
  ASSISTANT_ID_VOICE_RECEPTIONIST,
  SYSTEM_ASSISTANT_ID,
} from '../ai-assistant.constants';
import { AiVoiceFamily } from '../interfaces/config.interface';
import {
  FunctionAuthStrategyInterface,
  FunctionHeaderInterface,
} from '@vendasta/ai-assistants/lib/_internal/interfaces/function.interface';
import { VENDASTA_PARTNER } from '../ai-assistant.constants';

export function convertAssistantTypeToDefaultID(type: AssistantType): string {
  switch (type) {
    case AssistantType.ASSISTANT_TYPE_INBOX:
      return ASSISTANT_ID_CHAT_RECEPTIONIST;
    case AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING:
      return ASSISTANT_ID_CONTENT_CREATOR;
    case AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST:
      return ASSISTANT_ID_VOICE_RECEPTIONIST;
    case AssistantType.ASSISTANT_TYPE_SALES_COACH:
      return ASSISTANT_ID_SALES_COACH;
    case AssistantType.ASSISTANT_TYPE_SYSTEM:
      return SYSTEM_ASSISTANT_ID;
    default:
      return 'unspecified';
  }
}

export function AssistantIdToAssistantType(id: string): AssistantType {
  switch (id) {
    case ASSISTANT_ID_CHAT_RECEPTIONIST:
      return AssistantType.ASSISTANT_TYPE_INBOX;
    case ASSISTANT_ID_CONTENT_CREATOR:
      return AssistantType.ASSISTANT_TYPE_SOCIAL_MARKETING;
    case ASSISTANT_ID_VOICE_RECEPTIONIST:
      return AssistantType.ASSISTANT_TYPE_VOICE_RECEPTIONIST;
    case ASSISTANT_ID_SALES_COACH:
      return AssistantType.ASSISTANT_TYPE_SALES_COACH;
    default:
      return AssistantType.ASSISTANT_TYPE_CUSTOM;
  }
}

const OpenAISept17Voices = [
  {
    name: 'Alloy',
    value: 'alloy',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Openai%20audios/alloy.wav',
  },
  {
    name: 'Echo',
    value: 'echo',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Openai%20audios/echo.wav',
  },
  {
    name: 'Shimmer',
    value: 'shimmer',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Openai%20audios/shimmer.wav',
  },
];
const OpenAIDec17Voices = [
  {
    name: 'Ash',
    value: 'ash',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Openai%20audios/ash.wav',
  },
  {
    name: 'Ballad',
    value: 'ballad',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Openai%20audios/ballad.wav',
  },
  {
    name: 'Coral',
    value: 'coral',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Openai%20audios/coral.wav',
  },
  {
    name: 'Sage',
    value: 'sage',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Openai%20audios/sage.wav',
  },
  {
    name: 'Verse',
    value: 'verse',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Openai%20audios/verse.wav',
  },
];
const DeepgramAura1Voices = [
  {
    name: 'Asteria',
    value: 'aura-asteria-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Asteria.mp3',
  },
  {
    name: 'Luna',
    value: 'aura-luna-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Luna.mp3',
  },
  {
    name: 'Stella',
    value: 'aura-stella-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Stella.mp3',
  },
  {
    name: 'Athena',
    value: 'aura-athena-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Athena.mp3',
  },
  {
    name: 'Hera',
    value: 'aura-hera-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Hera.mp3',
  },
  {
    name: 'Orion',
    value: 'aura-orion-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Orion.mp3',
  },
  {
    name: 'Arcas',
    value: 'aura-arcas-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Arcas.mp3',
  },
  {
    name: 'Perseus',
    value: 'aura-perseus-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Perseus.mp3',
  },
  {
    name: 'Angus',
    value: 'aura-angus-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.IRELAND',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Angus.mp3',
  },
  {
    name: 'Orpheus',
    value: 'aura-orpheus-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Orpheus.mp3',
  },
  {
    name: 'Helios',
    value: 'aura-helios-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Helios.mp3',
  },
  {
    name: 'Zeus',
    value: 'aura-zeus-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Zeus.mp3',
  },
];
const DeepgramAura2Voices = [
  {
    name: 'Thalia',
    value: 'aura-2-thalia-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Thalia.mp3',
    recommended: true,
  },
  {
    name: 'Andromeda',
    value: 'aura-2-andromeda-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Andromeda.mp3',
    recommended: true,
  },
  {
    name: 'Helena',
    value: 'aura-2-helena-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Helena.mp3',
    recommended: true,
  },
  {
    name: 'Apollo',
    value: 'aura-2-apollo-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Apollo.mp3',
    recommended: true,
  },
  {
    name: 'Arcas',
    value: 'aura-2-arcas-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Arcas.mp3',
    recommended: true,
  },
  {
    name: 'Aries',
    value: 'aura-2-aries-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Aries.mp3',
    recommended: true,
  },
  {
    name: 'Amalthea',
    value: 'aura-2-amalthea-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.PHILIPPINES',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Amalthea.mp3',
  },
  {
    name: 'Asteria',
    value: 'aura-2-asteria-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Asteria.mp3',
  },
  {
    name: 'Athena',
    value: 'aura-2-athena-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Athena.mp3',
  },
  {
    name: 'Atlas',
    value: 'aura-2-atlas-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Atlas.mp3',
  },
  {
    name: 'Aurora',
    value: 'aura-2-aurora-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Aurora.mp3',
  },
  {
    name: 'Callista',
    value: 'aura-2-callista-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Callista.mp3',
  },
  {
    name: 'Cora',
    value: 'aura-2-cora-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Cora.mp3',
  },
  {
    name: 'Cordelia',
    value: 'aura-2-cordelia-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Cordelia.mp3',
  },
  {
    name: 'Delia',
    value: 'aura-2-delia-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Delia.mp3',
  },
  {
    name: 'Draco',
    value: 'aura-2-draco-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Draco.mp3',
  },
  {
    name: 'Electra',
    value: 'aura-2-electra-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Electra.mp3',
  },
  {
    name: 'Harmonia',
    value: 'aura-2-harmonia-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Harmonia.mp3',
  },
  {
    name: 'Hera',
    value: 'aura-2-hera-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Hera.mp3',
  },
  {
    name: 'Hermes',
    value: 'aura-2-hermes-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Hermes.mp3',
  },
  {
    name: 'Hyperion',
    value: 'aura-2-hyperion-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.AUSTRALIA',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Hyperion.mp3',
  },
  {
    name: 'Iris',
    value: 'aura-2-iris-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Iris.mp3',
  },
  {
    name: 'Janus',
    value: 'aura-2-janus-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Janus.mp3',
  },
  {
    name: 'Juno',
    value: 'aura-2-juno-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Juno.mp3',
  },
  {
    name: 'Jupiter',
    value: 'aura-2-jupiter-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Jupiter.mp3',
  },
  {
    name: 'Luna',
    value: 'aura-2-luna-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Luna.mp3',
  },
  {
    name: 'Mars',
    value: 'aura-2-mars-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Mars.mp3',
  },
  {
    name: 'Minerva',
    value: 'aura-2-minerva-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Minerva.mp3',
  },
  {
    name: 'Neptune',
    value: 'aura-2-neptune-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Neptune.mp3',
  },
  {
    name: 'Odysseus',
    value: 'aura-2-odysseus-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Odysseus.mp3',
  },
  {
    name: 'Ophelia',
    value: 'aura-2-ophelia-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Ophelia.mp3',
  },
  {
    name: 'Orion',
    value: 'aura-2-orion-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Orion.mp3',
  },
  {
    name: 'Orpheus',
    value: 'aura-2-orpheus-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Orpheus.mp3',
  },
  {
    name: 'Pandora',
    value: 'aura-2-pandora-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Pandora.mp3',
  },
  {
    name: 'Phoebe',
    value: 'aura-2-phoebe-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Phoebe.mp3',
  },
  {
    name: 'Pluto',
    value: 'aura-2-pluto-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Pluto.mp3',
  },
  {
    name: 'Saturn',
    value: 'aura-2-saturn-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Saturn.mp3',
  },
  {
    name: 'Selene',
    value: 'aura-2-selene-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Selene.mp3',
  },
  {
    name: 'Theia',
    value: 'aura-2-theia-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.AUSTRALIA',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Theia.mp3',
  },
  {
    name: 'Vesta',
    value: 'aura-2-vesta-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Vesta.mp3',
  },
  {
    name: 'Zeus',
    value: 'aura-2-zeus-en',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Deepgram%20audios/Deepgram%20Aura2%20Zeus.mp3',
  },
];

const ElevenLabsVoices = [
  {
    name: 'Aria',
    value: '9BWtsMINqrJLrRacOk9x',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/aria.mp3',
  },
  {
    name: 'Sarah',
    value: 'EXAVITQu4vr4xnSDxMaL',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/sarah.mp3',
  },
  {
    name: 'Laura',
    value: 'FGY2WhTYpPnrIDTdsKH5',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/laura.mp3',
  },
  {
    name: 'Charlie',
    value: 'IKne3meq5aSn9XLyUdCD',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.AUSTRALIA',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/charlie.mp3',
  },
  {
    name: 'George',
    value: 'JBFqnCBsd6RMkjVDRZzb',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/george.mp3',
  },
  {
    name: 'Callum',
    value: 'N2lVS1w4EtoT3dr4eOWO',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/callum.mp3',
  },
  {
    name: 'River',
    value: 'SAz9YHcvj6GT2YYXdXww',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/river.mp3',
  },
  {
    name: 'Liam',
    value: 'TX3LPaxmHKxFdv7VOQHJ',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/liam.mp3',
  },
  {
    name: 'Charlotte',
    value: 'XB0fDUnXU5powFXDhCwa',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.SWEDEN',
    previewAudioUrl:
      'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/charlotte.mp3',
  },
  {
    name: 'Alice',
    value: 'Xb7hH8MSUJpSbSDYk0k2',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/alice.mp3',
  },
  {
    name: 'Matilda',
    value: 'XrExE9yKIg1WjnnlVkGX',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/matilda.mp3',
  },
  {
    name: 'Will',
    value: 'bIHbv24MWmeRgasZH58o',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/will.mp3',
  },
  {
    name: 'Jessica',
    value: 'cgSgspJ2msm6clMCkdW9',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/jessica.mp3',
  },
  {
    name: 'Eric',
    value: 'cjVigY5qzO86Huf0OWal',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/eric.mp3',
  },
  {
    name: 'Chris',
    value: 'iP95p4xoKVk53GoZ742B',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/chris.mp3',
  },
  {
    name: 'Brian',
    value: 'nPczCjzI2devNBz1zQrb',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/brian.mp3',
  },
  {
    name: 'Daniel',
    value: 'onwK4e9ZLuTAKqWW03F9',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.UK',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/daniel.mp3',
  },
  {
    name: 'Lily',
    value: 'pFZP5JQG7iQjIQuC4Bku',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.FEMALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.ITALY',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/lily.mp3',
  },
  {
    name: 'Bill',
    value: 'pqHfZKP75CvOlQylNhV4',
    gender: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.GENDER.MALE',
    region: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.REGION.US',
    previewAudioUrl: 'https://storage.googleapis.com/voice-receptionist-preview-audios/Elevenlabs%20audios/bill.mp3',
  },
];

export const voiceFamilies: AiVoiceFamily[] = [
  {
    name: 'ElevenLabs Flash v2.5',
    provider: VendorModel.VENDOR_MODEL_ELEVEN_LABS,
    description_key: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.FAMILY_DESCRIPTION.ELEVEN_LABS_FLASH_2_5',
    voices: ElevenLabsVoices,
  },
  {
    name: 'Deepgram Aura-2',
    provider: VendorModel.VENDOR_MODEL_DEEPGRAM,
    description_key: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.FAMILY_DESCRIPTION.DEEPGRAM_AURA_2',
    voices: DeepgramAura2Voices,
    recommended: true,
  },
  {
    name: 'Deepgram Aura-1',
    provider: VendorModel.VENDOR_MODEL_DEEPGRAM,
    description_key: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.FAMILY_DESCRIPTION.DEEPGRAM_AURA',
    voices: DeepgramAura1Voices,
  },
  {
    name: 'OpenAI GPT-4o Realtime (Dec. 17 Preview)',
    provider: VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME,
    description_key: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.FAMILY_DESCRIPTION.GPT_RT_PREVIEW_DEC_2024',
    voices: OpenAIDec17Voices,
  },
  {
    name: 'OpenAI GPT-4o Realtime (Sep. 17 Preview)',
    provider: VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME,
    description_key: 'AI_ASSISTANT.SETTINGS.VOICE_CONFIG.FAMILY_DESCRIPTION.GPT_RT_PREVIEW_SEPT_2024',
    voices: OpenAISept17Voices,
  },
];

export function getVoiceFamilies(deepgramEnabled: boolean, elevenLabsEnabled: boolean): AiVoiceFamily[] {
  const results: AiVoiceFamily[] = [];
  for (const family of voiceFamilies) {
    if (family.provider === VendorModel.VENDOR_MODEL_OPEN_AI_REALTIME) {
      results.push(family);
    }
    if (family.provider === VendorModel.VENDOR_MODEL_DEEPGRAM && deepgramEnabled) {
      results.push(family);
    }
    if (family.provider === VendorModel.VENDOR_MODEL_ELEVEN_LABS && elevenLabsEnabled) {
      results.push(family);
    }
  }
  return results;
}

export function familyFromVoice(voice?: string) {
  return voiceFamilies.find((family) => family.voices.find((v) => v.value === voice));
}

export function voicesForFamily(family: string) {
  return voiceFamilies.find((f) => f.name === family)?.voices || [];
}

export function previewUrlFromVoice(voice: string) {
  for (const family of voiceFamilies) {
    const foundVoice = family.voices.find((v) => v.value === voice);
    if (foundVoice) {
      return foundVoice.previewAudioUrl;
    }
  }
  return undefined;
}

export function getNamespace(accountGroupId: string | undefined, partnerId: string | undefined): Namespace | undefined {
  if (accountGroupId) {
    return new Namespace({
      accountGroupNamespace: { accountGroupId },
    });
  }
  if (partnerId === VENDASTA_PARTNER) {
    return new Namespace({
      systemNamespace: {},
    });
  }
  if (partnerId) {
    return new Namespace({
      partnerNamespace: { partnerId },
    });
  }
  return;
}

export const globalNamespace = new Namespace({
  globalNamespace: {},
});

export class AIFunction implements FunctionInterface {
  id?: string;
  namespace?: NamespaceInterface;
  description?: string;
  url?: string;
  methodType?: string;
  functionParameters?: FunctionParameterInterface[];
  headers?: FunctionHeaderInterface[];
  managed?: boolean;
  generatesAnswer?: boolean;
  updated?: Date;
  authStrategy?: FunctionAuthStrategyInterface;
}
